import { isMarkKey, markFieldOf, markFilterKey } from "@/lib/filter-model";

/**
 * The fields the Clicks filter bar offers, in groups (owner, 27.10.2026: one
 * list with sections, like Group by). A key is a filter key - what
 * clicks_filter_sql and search_filter_values take, i.e. public.clicks_dimensions()
 * - and a label matches Group by's. Landing page is left out: Keitaro never
 * fills that column, so its only value would be "Unknown".
 */
export interface FilterField {
  key: string;
  label: string;
  /** A fixed list instead of the server's values (marks); count = how many carry it. */
  options?: { value: string; label: string; count?: number }[];
  /** How a value reads on the chip: "suspicious" → "Suspicious". */
  valueLabel?: (value: string) => string;
}

export const FILTER_FIELD_GROUPS: { title: string; fields: FilterField[] }[] = [
  {
    title: "Site",
    fields: [
      { key: "brand", label: "Brand" },
      { key: "domain", label: "Domain" },
      { key: "url", label: "URL" },
    ],
  },
  {
    title: "Keitaro",
    fields: [
      { key: "campaign_name", label: "Campaign" },
      { key: "campaign_group", label: "Campaign Group" },
      { key: "stream", label: "Flow (Stream)" },
      { key: "offer", label: "Offer" },
      { key: "affiliate_network", label: "Affiliate Network" },
      { key: "source", label: "Traffic Source" },
    ],
  },
  {
    title: "GEO & language",
    fields: [
      { key: "country", label: "Country (GEO)" },
      { key: "region", label: "Region" },
      { key: "city", label: "City" },
      { key: "language", label: "Language" },
    ],
  },
];

/** Shown as empty "+ Field" slots when not in use (owner, 27.10.2026). */
export const QUICK_FILTER_KEYS = ["brand", "country", "campaign_name"];

const LABELS = new Map(FILTER_FIELD_GROUPS.flatMap((g) => g.fields).map((f) => [f.key, f.label]));

/** A field's label; an unknown key (an old link) shows as itself; "mark:domain" is "Domain mark". */
export const filterFieldLabel = (key: string): string =>
  isMarkKey(key) ? `${filterFieldLabel(markFieldOf(key))} mark` : (LABELS.get(key) ?? key);

/** Row marks (20261030183128): shared by everyone, three kinds (owner, 30.10.2026). */
export type MarkKind = "suspicious" | "watch" | "pause";
export const MARKS: { value: MarkKind; label: string }[] = [
  { value: "suspicious", label: "Suspicious" },
  { value: "watch", label: "Watch" },
  { value: "pause", label: "Pause" },
];
const markLabel = (v: string): string => MARKS.find((m) => m.value === v)?.label ?? v;

/**
 * "Domain mark is Suspicious": the filter on the marks of one field's values.
 * Counts only when known (the grouping's own field): a 0 would claim nobody
 * marked anything.
 */
export const markField = (field: string, counts?: Partial<Record<string, number>>): FilterField => ({
  key: markFilterKey(field),
  label: filterFieldLabel(markFilterKey(field)),
  options: MARKS.map((m) => ({ ...m, count: counts ? (counts[m.value] ?? 0) : undefined })),
  valueLabel: markLabel,
});
